import { useEffect, useMemo, useState } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import { escapeRegex } from 'lib/string/regexUtils'
import Image from 'components/Common/Image'

import type { MessagePart } from './Message'
import Message from './Message'
import { stripContextTags } from 'tripPlanner/utils/contextTag'
import AssistantResponseToolbar from './AssistantResponseToolbar'
import { MESSAGE_ROLES, PART_TYPES, UI_COMPONENT_TYPES } from './constants'
import BodyText from 'LuxKit/Typography/BodyText'
import {
  getChatToolLoadingMessageDescriptor,
  type ChatToolLoadingKey,
} from 'tripPlanner/config/chatToolLoading'
import { EMPTY_SUMMARIES, type ChatLoadingStatus } from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/chatStream'
import { TOOL_NAME } from 'tripPlanner/api/types'

const AssistantToolbarWrapper = styled.div`
  margin-top: ${rem(-12)};
`

export interface MessageItem {
  id: string
  parts: Array<MessagePart>
  role: 'user' | 'assistant'
  messageType?: string
  isMarkdown?: boolean
  files?: Array<File>
  rawContent?: string
  oaiConversationId?: string
}

interface MessagesListProps {
  messages: Array<MessageItem>
  isLoading?: boolean
  isUpdatingItinerary?: boolean
  /** Toolbar callbacks — consumed by AssistantResponseToolbar */
  onRegenerate?: () => void
  onCopy?: () => void
  disableRegenerateButton?: boolean
  disableCopyButton?: boolean
  showAssistantActions?: boolean
  copiedRecently?: boolean
  onThumbsUp?: () => void
  onThumbsDown?: () => void
  feedbackSubmitted?: 'UP' | 'DOWN'
  /**
   * Live multi-choice selection from the page.
   * Applied to the last message only; older messages use their
   * historical selection computed from message content.
   */
  externalSelection?: Array<string>
  activeToolLoadingKey?: ChatToolLoadingKey
  /** Stream active but no chunks received for 10+ seconds. */
  isStalled?: boolean
  /** Device network status via navigator.onLine. */
  isOnline?: boolean
  /** Cycling summary messages from Groq for the reasoning phase. */
  reasoningSummaries?: ReadonlyArray<string>
  /** Current tool call info for the tool_calling phase. */
  toolCallInfo?: { tool: string; args: Record<string, unknown> }
  /** Current loading status for phase-specific fallback labels. */
  loadingStatus?: ChatLoadingStatus
}

interface MessageMetadata {
  previousRole: 'user' | 'assistant' | undefined
  isLastMessage: boolean
  multiChoiceInitialSelection: Array<string> | undefined
  previousChoiceOptions: Array<string> | undefined
}

function getChoiceOptions(parts: Array<MessagePart>): Array<string> {
  const allOptions: Array<string> = []
  for (const part of parts) {
    if (
      part.type === PART_TYPES.UI_COMPONENT &&
      (part.data.type === UI_COMPONENT_TYPES.PILLS_LIST ||
        part.data.type === UI_COMPONENT_TYPES.CARDS_LIST)
    ) {
      const options = part.data.options.map((opt: { label: string } | { title: string }) => {
        return 'label' in opt ? opt.label : opt.title
      })
      allOptions.push(...options)
    }
  }
  return allOptions
}

function parseSelectedLabels(userContent: string, options: Array<string>): Array<string> {
  return options.filter(label => {
    const pattern = new RegExp(`(^|\\s|,|\\n)${escapeRegex(label)}(\\s|,|\\n|$)`, 'i')
    return pattern.test(userContent)
  })
}

function buildMessageMetadata(messages: Array<MessageItem>): Array<MessageMetadata> {
  return messages.map((message, messageIndex) => {
    const previousMessage = messageIndex > 0 ? messages[messageIndex - 1] : undefined
    const previousRole = previousMessage?.role
    const isLastMessage = messageIndex === messages.length - 1
    let multiChoiceInitialSelection: Array<string> | undefined

    if (message.role === MESSAGE_ROLES.ASSISTANT && !isLastMessage) {
      const nextMessage = messages[messageIndex + 1]
      if (nextMessage?.role === 'user') {
        const options = getChoiceOptions(message.parts)
        if (options.length > 0) {
          const userContent =
            nextMessage.rawContent ||
            nextMessage.parts
              .filter((part): part is Extract<MessagePart, { type: 'text' }> => part.type === PART_TYPES.TEXT)
              .map(textPart => textPart.content)
              .join('')
          multiChoiceInitialSelection = parseSelectedLabels(stripContextTags(userContent), options)
        }
      }
    }

    const previousChoiceOptions =
      message.role === MESSAGE_ROLES.USER && previousRole === MESSAGE_ROLES.ASSISTANT
        ? getChoiceOptions(previousMessage?.parts ?? [])
        : undefined

    return {
      previousRole,
      isLastMessage,
      multiChoiceInitialSelection,
      previousChoiceOptions,
    }
  })
}

function useCyclingSummaries(summaries: ReadonlyArray<string>, intervalMs = 5000): string | undefined {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    setIndex(0)
    if (summaries.length <= 1) return
    const id = setInterval(() => {
      setIndex(prev => (prev + 1) % summaries.length)
    }, intervalMs)
    return () => clearInterval(id)
  }, [summaries, intervalMs])

  if (summaries.length === 0) return undefined
  return summaries[index % summaries.length]
}

export function formatToolCallLabel(
  tool: string,
  args: Record<string, unknown>,
  intl: IntlShape,
): string {
  // naturalLanguageQuery is the model's search query — already a user-friendly description
  // like "romantic hotels in Nusa Dua Bali". Use it directly as the label, prefixed with
  // "Searching for". Don't embed it in "Searching for hotels in {query}" which doubles up.
  const query = typeof args.naturalLanguageQuery === 'string' ? args.naturalLanguageQuery : ''

  switch (tool) {
    case TOOL_NAME.SEARCH_HOTELS:
      return query
        ? intl.formatMessage({ id: 'zIE_2P8AJ2eqM2d1hwT2', defaultMessage: 'Searching for {query}\u2026', description: 'Loading label when AI is searching for hotels with a specific query in trip planner chat' }, { query })
        : intl.formatMessage({ id: 'DJzTyh_Cm-_h6NCjCUmI', defaultMessage: 'Searching for hotels\u2026', description: 'Loading label when AI is searching for hotels without a specific query in trip planner chat' })
    case TOOL_NAME.SEARCH_EXPERIENCES:
      return query
        ? intl.formatMessage({ id: 'sEyzTQylegUCkZA-FdYx', defaultMessage: 'Searching for {query}\u2026', description: 'Loading label when AI is searching for experiences with a specific query in trip planner chat' }, { query })
        : intl.formatMessage({ id: 'NxKjXbGX7AGf3taYwvC5', defaultMessage: 'Finding experiences\u2026', description: 'Loading label when AI is searching for experiences without a specific query in trip planner chat' })
    case TOOL_NAME.SEARCH_FLIGHTS:
      return intl.formatMessage({ id: '8wdm_RdhsHFMyJbPaXDV', defaultMessage: 'Looking up flights\u2026', description: 'Loading label when AI is searching for flights in trip planner chat' })
    case TOOL_NAME.GET_ORDERS:
      return intl.formatMessage({ id: '1irTo-3lMOEBMw4yiVVT', defaultMessage: 'Looking through your order history\u2026', description: 'Loading label when AI is looking through the user order history in trip planner chat' })
    case TOOL_NAME.LOOKUP_OFFER:
      return intl.formatMessage({ id: '1QWQulSeYQUQkM4SvjVW', defaultMessage: 'Looking up offer details\u2026', description: 'Loading label when AI is looking up a specific offer in trip planner chat' })
    case TOOL_NAME.GET_OFFER_REVIEWS:
      return intl.formatMessage({ id: 'ebmZeVTxguWsK-a1E0TM', defaultMessage: 'Reading recent reviews\u2026', description: 'Loading label when AI is reading hotel or experience reviews in trip planner chat' })
    case TOOL_NAME.LOOKUP_OFFER_GENERAL_AVAILABILITY:
      return intl.formatMessage({ id: '7xsHWyix6HjMwlgHuVlc', defaultMessage: 'Checking broad availability\u2026', description: 'Loading label when AI is checking broad hotel or experience availability in trip planner chat' })
    case TOOL_NAME.LOOKUP_OFFER_SPECIFIC_AVAILABILITY:
      return intl.formatMessage({ id: 'VHAFyRwISyGY6D4QYFsC', defaultMessage: 'Checking exact availability\u2026', description: 'Loading label when AI is checking exact hotel or experience availability in trip planner chat' })
    default:
      return intl.formatMessage({ id: '6c5xn35ovrdi7Qqa22_o', defaultMessage: 'Working on it\u2026', description: 'Generic loading label when AI is executing an unknown tool in trip planner chat' })
  }
}

function MessagesList({
  messages,
  isLoading = false,
  isUpdatingItinerary = false,
  onRegenerate = noop,
  onCopy = noop,
  disableRegenerateButton = false,
  disableCopyButton = false,
  showAssistantActions = false,
  copiedRecently = false,
  onThumbsUp = noop,
  onThumbsDown = noop,
  feedbackSubmitted,
  externalSelection,
  activeToolLoadingKey,
  isStalled = false,
  isOnline = true,
  reasoningSummaries = EMPTY_SUMMARIES,
  toolCallInfo,
  loadingStatus = 'idle',
}: MessagesListProps) {
  const intl = useIntl()
  const isThumbsUp = feedbackSubmitted === 'UP'
  const isThumbsDown = feedbackSubmitted === 'DOWN'
  const isThumbsIconDisabled = feedbackSubmitted !== undefined
  const currentSummary = useCyclingSummaries(reasoningSummaries)

  const toolCallLabel = useMemo(() => {
    if (!toolCallInfo) return undefined
    return formatToolCallLabel(toolCallInfo.tool, toolCallInfo.args, intl)
  }, [toolCallInfo, intl])

  const connectionStatusLabel = useMemo(() => {
    if (!isOnline) {
      return intl.formatMessage({
        id: 'p7Quu-YJBMFaMHUl1gYI',
        defaultMessage: "You're offline — will retry when connected",
        description: 'Loading indicator label when device has no network connection during chat streaming',
      })
    }
    if (isStalled) {
      return intl.formatMessage({
        id: 'LJCUCz9svpwcnjXJ0-Oc',
        defaultMessage: 'Taking longer than expected\u2026',
        description: 'Loading indicator label when the AI stream has stalled (no data for 10+ seconds)',
      })
    }
    return undefined
  }, [isOnline, isStalled, intl])

  const toolLoadingLabel = useMemo(() => {
    if (!activeToolLoadingKey) return undefined
    return intl.formatMessage(
      getChatToolLoadingMessageDescriptor(activeToolLoadingKey),
    )
  }, [activeToolLoadingKey, intl])

  const reasoningFallbackLabel = useMemo(() => {
    if (loadingStatus !== 'reasoning' || currentSummary) return undefined
    return intl.formatMessage({
      id: 'lj2_OxUq7xoaw2FGp47h',
      defaultMessage: 'Thinking\u2026',
      description: 'Fallback loading label during AI reasoning when Groq summaries are unavailable in trip planner chat',
    })
  }, [loadingStatus, currentSummary, intl])

  const messageMetadata = useMemo(() => buildMessageMetadata(messages), [messages])

  const loadingLabel = connectionStatusLabel ?? toolCallLabel ?? currentSummary ?? reasoningFallbackLabel ?? toolLoadingLabel

  return (
    <Group direction="vertical" gap={24} fullWidth>
      {
        messages.map((message, messageIndex) => {
          const { isLastMessage, multiChoiceInitialSelection, previousChoiceOptions } = messageMetadata[messageIndex]

          return (
            <Message
              messageId={message.id}
              key={message.id}
              parts={message.parts}
              role={message.role}
              isMarkdown={message.isMarkdown}
              files={message.files}
              rawContent={message.rawContent}
              oaiConversationId={message.oaiConversationId}
              isLastMessage={isLastMessage}
              multiChoiceInitialSelection={multiChoiceInitialSelection}
              externalSelection={externalSelection}
              previousChoiceOptions={previousChoiceOptions}
            />
          )
        })
      }
      {showAssistantActions &&
        <AssistantToolbarWrapper>
          <AssistantResponseToolbar
            onCopy={onCopy}
            disableCopyButton={disableCopyButton}
            copiedRecently={copiedRecently}
            onRegenerate={onRegenerate}
            disableRegenerateButton={disableRegenerateButton}
            onThumbsUp={onThumbsUp}
            onThumbsDown={onThumbsDown}
            isThumbsIconDisabled={isThumbsIconDisabled}
            isThumbsUp={isThumbsUp}
            isThumbsDown={isThumbsDown}
          />
        </AssistantToolbarWrapper>}
      {isLoading && isUpdatingItinerary && (
        <Group direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="start">
          <Image
            id="0jst326hyikkdg7pf9n"
            width={40}
            height={40}
            alt={intl.formatMessage({
              id: 'Vyc6L4HykGywzgKBGcRY',
              defaultMessage: 'Updating your itinerary',
              description: 'Loading message while AI updates the trip itinerary',
            })}
            format="gif"
          />
          <BodyText variant="medium" weight="bold">{intl.formatMessage({
            id: 'Vyc6L4HykGywzgKBGcRY',
            defaultMessage: 'Updating your itinerary',
            description: 'Loading message while AI updates the trip itinerary',
          })}
          </BodyText>
        </Group>
      )}
      {isLoading && !isUpdatingItinerary && (
        <Group direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="start">
          <Image
            id="2cxvqkxsvq3fhnk5lsht"
            width={40}
            height={40}
            alt={loadingLabel ?? intl.formatMessage({
              id: 'zhhQhC7Y2M6v94sHcPAS',
              defaultMessage: 'AI Trip assistant is typing',
              description: 'Alt text for loading animation while AI assistant is composing a response',
            })}
            format="gif"
          />
          {loadingLabel && (
            <BodyText variant="medium" weight="bold">
              {loadingLabel}
            </BodyText>
          )}
        </Group>
      )}
    </Group>
  )
}

export default MessagesList
